/*
 * VividSound custom scripts.
 *
 * Loaded deferred from layout/theme.liquid, after theme.js.
 * Prefix: vs-* for custom elements, data-vs-* for hooks.
 */

/* ---------------------------------------------------------------------------
 * Trust lines (snippets/vs-trust-lines.liquid)
 *
 * Delivery dates and the cart's "Ships …": storefront pages are cached, so what Liquid printed
 * can be a day old.
 * They are recomputed here with the same rule, in Amsterdam time: dispatch today if it's a
 * shipping day before the cutoff, otherwise the next shipping day, then add the market's
 * delivery days. Shipping and delivery days: never Sunday, Saturday only when the market
 * delivers on Saturday (data-saturday).
 *
 * Variant change: the theme only swaps its own blocks. The variant's section HTML already holds
 * the lines rendered for that variant (hidden when it's sold out), so they are copied over from
 * there. If they are missing from it, the variant's available flag decides.
 * ------------------------------------------------------------------------ */

const VsDelivery = {
  timeZone: 'Europe/Amsterdam',

  // Today's date and hour in Amsterdam, as a UTC midnight Date plus the hour.
  today() {
    const parts = {};
    new Intl.DateTimeFormat('en-GB', {
      timeZone: this.timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      hourCycle: 'h23'
    })
      .formatToParts(new Date())
      .forEach((part) => {
        parts[part.type] = part.value;
      });
    return {
      date: new Date(Date.UTC(+parts.year, +parts.month - 1, +parts.day)),
      hour: +parts.hour
    };
  },

  // Shipping and delivery day: never Sunday, Saturday only when the market delivers on Saturday.
  isOpen(date, saturday) {
    return date.getUTCDay() !== 0 && (date.getUTCDay() !== 6 || saturday);
  },

  nextDeliveryDay(date, saturday) {
    const next = new Date(date);
    do {
      next.setUTCDate(next.getUTCDate() + 1);
    } while (!this.isOpen(next, saturday));
    return next;
  },

  // Dispatch day: today if it's a shipping day before the cutoff, otherwise the next one.
  dispatchDay(cutoff, saturday) {
    const { date, hour } = this.today();
    if (this.isOpen(date, saturday) && hour < cutoff) return { date, days: 0 };
    const next = this.nextDeliveryDay(date, saturday);
    return { date: next, days: Math.round((next - date) / 86400000) };
  },

  format(date) {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  },

  update(root = document) {
    root.querySelectorAll('[data-vs-delivery]').forEach((el) => {
      const min = parseInt(el.dataset.min, 10);
      const max = parseInt(el.dataset.max, 10);
      const cutoff = parseInt(el.dataset.cutoff, 10);
      if ([min, max, cutoff].some(Number.isNaN)) return;

      const saturday = el.hasAttribute('data-saturday');
      let day = this.dispatchDay(cutoff, saturday).date;
      let from = day;
      for (let i = 1; i <= max; i += 1) {
        day = this.nextDeliveryDay(day, saturday);
        if (i === min) from = day;
      }

      const fromEl = el.querySelector('[data-vs-date-from]');
      const toEl = el.querySelector('[data-vs-date-to]');
      if (fromEl) fromEl.textContent = this.format(from);
      if (toEl) toEl.textContent = this.format(day);
    });

    // Cart: "Ships today", "Ships tomorrow" or, further away, "Ships Monday".
    root.querySelectorAll('[data-vs-dispatch]').forEach((el) => {
      const cutoff = parseInt(el.dataset.cutoff, 10);
      if (Number.isNaN(cutoff)) return;

      const { days } = this.dispatchDay(cutoff, el.hasAttribute('data-saturday'));
      el.textContent = [el.dataset.today, el.dataset.tomorrow][days] || el.dataset.monday;
    });
  }
};

VsDelivery.update();

if (window.theme && theme.pubsub) {
  theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange, ({ data }) => {
    const id = `VsTrustLines-${data.productId}`;
    const current = document.getElementById(id);
    if (!current) return;

    const next = data.parsedHTML && data.parsedHTML.getElementById(id);
    if (next) {
      current.innerHTML = next.innerHTML;
      current.hidden = next.hidden;
      VsDelivery.update(current);
    } else {
      current.hidden = !(data.variant && data.variant.available);
    }
  });

  // The cart drawer and cart page swap in fresh HTML after a cart change; date it after the swap.
  theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, () => {
    setTimeout(() => VsDelivery.update());
  });
}
